import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { CONSTANTS } from '../../../../constants/constants';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import NRArrowForwardIcon from '../../../../NRIcons/NRArrowForwardIcon';
import NRShortenFilePathStyles from './NRShortenFilePath.styles';
import FileDownloadComponent from '../../../../components/NRCertTable/CertTableCustomComponents/FileDownloadComponent';
import { shortenPath } from '../../../../utils/utils';
import NRShortenedPathTooltip from './NRShortenedPathTooltip';

const useStyles = makeStyles(NRShortenFilePathStyles);

const NRShortenFilePath = ({ filePath, shouldDisplayLink = false, onClickLink, customMinWidth }) => {
  const theme = useTheme();
  const classes = useStyles();
  const containerRef = useRef(null);
  const [maxWidth, setMaxWidth] = useState(300);
  const [isHovered, setIsHovered] = useState(false); // State to track hover
  const { enqueueSnackbar } = useSnackbar();
  const { FILEPATH_TOOLTIP } = CONSTANTS.TABLE.VULNERABILITIES_TABLE;
  const { componentId } = useParams();

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  function handleOnClick(e) {
    e.stopPropagation();
    navigator.clipboard.writeText(filePath);
    enqueueSnackbar('copied!', { variant: 'success', preventDuplicate: true, autoHideDuration: 1000 });
  }

  useEffect(() => {
    const updateMaxWidth = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const adjustedWidth = containerWidth - 55;
        setMaxWidth(adjustedWidth);
      }
    };

    updateMaxWidth();
    window.addEventListener('resize', updateMaxWidth);
    return () => {
      window.removeEventListener('resize', updateMaxWidth);
    };
  }, []);

  return (
    <Box onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className={classes.tooltipContainer}>
      <NRShortenedPathTooltip
        filePath={filePath}
        containerRef={containerRef}
        contentClass={classes.tooltipContent}
        textClass={classes.shortenPath}
        customMinWidth={customMinWidth}
        maxWidth={maxWidth}
      />
      <Box className={clsx(classes.iconsContainer, { [classes.showOnHover]: isHovered })}>
        {filePath && (
          <Tooltip enterTouchDelay={1500} followCursor={true} title={CONSTANTS.COPY_PATH_TO_CLIPBOARD} placement={'top-start'}>
            <FileCopyOutlinedIcon
              size={16}
              className={classes.copyIcon}
              color={theme.palette.text.icon}
              style={{ alignSelf: 'center', marginLeft: '4px', cursor: 'pointer' }}
              onClick={handleOnClick}
            />
          </Tooltip>
        )}
        {filePath && shouldDisplayLink && (
          <Tooltip enterTouchDelay={1500} followCursor={true} title={FILEPATH_TOOLTIP} placement={'top-start'}>
            <Box onClick={onClickLink} className={classes.linkContainer}>
              <NRArrowForwardIcon color={theme.palette.text.icon} />
            </Box>
          </Tooltip>
        )}
        {filePath && componentId && (
          <Tooltip enterTouchDelay={1500} followCursor={true} title={'Download'} placement={'top-start'}>
            <Box className={classes.linkContainer}>
              <FileDownloadComponent componentId={componentId} filePath={filePath} cursorPointer={true} />
            </Box>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default NRShortenFilePath;
